import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function CreateMarkingProject() {
  const navigate = useNavigate();
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [formData, setFormData] = useState({
    project_name: '',
    curriculum: 'Cambridge',
    subject: '',
    grade_level: 'Year 4',
    total_marks: ''
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!agreedToTerms) {
      toast.error('Please agree to the Responsible Use terms');
      return;
    }

    if (!formData.project_name.trim()) {
      toast.error('Please enter a project name');
      return;
    }

    if (!formData.total_marks || formData.total_marks <= 0) {
      toast.error('Please enter valid total marks');
      return;
    }

    try {
      setCreating(true);
      const response = await axios.post(
        `${API}/marking/projects`,
        {
          project_name: formData.project_name,
          curriculum: formData.curriculum,
          subject: formData.subject || null,
          grade_level: formData.grade_level,
          total_marks: parseFloat(formData.total_marks)
        },
        { withCredentials: true }
      );

      toast.success('Project created successfully!');
      navigate(`/assessment/project/${response.data.project_id}`);
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  if (showDisclaimer) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        
        <section className="py-16 px-6 sm:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl border-2 border-amber-200 p-8 shadow-lg">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">
                    AI Marking Assistant – Responsible Use Notice
                  </h2>
                  <p className="text-sm text-slate-600">
                    Please read and agree before continuing
                  </p>
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl p-6 mb-6 space-y-4 text-sm text-slate-700 leading-relaxed">
                <p>
                  This tool is designed to assist teachers in marking their own <strong>classroom assessments, practice tests, homework, quizzes, and formative assessments</strong>.
                </p>

                <p className="font-semibold text-slate-900">
                  ⚠️ Do not upload:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Confidential school examination papers</li>
                  <li>Board examinations or standardized tests</li>
                  <li>Copyrighted assessment materials</li>
                  <li>Any assessments you do not have permission to use</li>
                </ul>

                <div className="border-t border-slate-200 pt-4 mt-4">
                  <p className="font-semibold text-slate-900 mb-2">Important Guidelines:</p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">•</span>
                      <span>The teacher is responsible for ensuring they have the legal right to upload the assessment and any student work</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">•</span>
                      <span>AI-generated marks are <strong>suggestions only</strong> and must always be reviewed and approved by the teacher before being used or shared</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">•</span>
                      <span>Do not rely on this tool as the sole basis for high-stakes grading, promotion decisions, or official examination results</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">•</span>
                      <span>Uploaded test papers will NOT be used to train any AI model</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <p className="font-semibold text-blue-900 mb-2">By selecting "I Agree", you confirm that:</p>
                  <ul className="space-y-1 text-sm text-blue-800">
                    <li>✓ You have permission to upload the assessment and student work</li>
                    <li>✓ The uploaded material is for educational purposes within your classroom</li>
                    <li>✓ You will review all AI-generated marks before finalising results</li>
                    <li>✓ You understand that Learn with Zeenat provides an AI-assisted marking tool and is not responsible for final grading decisions</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-center gap-3 mb-6">
                <input
                  type="checkbox"
                  id="agree"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="w-5 h-5 rounded border-2 border-slate-300 text-purple-600 focus:ring-2 focus:ring-purple-100"
                />
                <label htmlFor="agree" className="text-base font-semibold text-slate-900 cursor-pointer">
                  I have read and agree to the Responsible Use terms
                </label>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => navigate('/assessment')}
                  className="flex-1 bg-slate-100 text-slate-700 hover:bg-slate-200 px-6 py-3 rounded-xl font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setShowDisclaimer(false)}
                  disabled={!agreedToTerms}
                  className={`flex-1 px-6 py-3 rounded-xl font-semibold transition-all ${
                    agreedToTerms
                      ? 'bg-purple-600 text-white hover:bg-purple-700 shadow-sm active:scale-95'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  Continue to Create Project
                </button>
              </div>
            </div>
          </div>
        </section>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      <section className="py-16 px-6 sm:px-8 lg:px-12">
        <div className="max-w-3xl mx-auto">
          {/* Reminder Banner */}
          <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4 mb-8">
            <p className="text-sm text-blue-900 font-medium">
              📝 For classroom assessments only. Teacher review is required before finalising marks.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-sm">
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Create Marking Project</h1>
            <p className="text-base text-slate-600 mb-8">
              Set up your AI-assisted test marking project
            </p>

            <form onSubmit={handleCreate} className="space-y-6">
              <div>
                <label className="block text-base font-semibold text-slate-900 mb-2">
                  Project Name *
                </label>
                <input
                  type="text"
                  value={formData.project_name}
                  onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                  placeholder="e.g., Grade 4 Math Quiz - Week 5"
                  className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-base focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none transition-all"
                  required
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-base font-semibold text-slate-900 mb-2">
                    Curriculum *
                  </label>
                  <select
                    value={formData.curriculum}
                    onChange={(e) => setFormData({ ...formData, curriculum: e.target.value })}
                    className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-base focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none transition-all"
                    required
                  >
                    <option value="Cambridge">Cambridge</option>
                    <option value="IB">IB (International Baccalaureate)</option>
                    <option value="CBSE">CBSE (India)</option>
                    <option value="ICSE">ICSE (India)</option>
                    <option value="Oxford">Oxford</option>
                    <option value="National Curriculum">National Curriculum (UK)</option>
                    <option value="Custom">Custom</option>
                  </select>
                </div>

                <div>
                  <label className="block text-base font-semibold text-slate-900 mb-2">
                    Year Level *
                  </label>
                  <select
                    value={formData.grade_level}
                    onChange={(e) => setFormData({ ...formData, grade_level: e.target.value })}
                    className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-base focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none transition-all"
                    required
                  >
                    <option value="Year 2">Year 2</option>
                    <option value="Year 3">Year 3</option>
                    <option value="Year 4">Year 4</option>
                    <option value="Year 5">Year 5</option>
                    <option value="Year 6">Year 6</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-base font-semibold text-slate-900 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="e.g., Mathematics, Science"
                    className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-base focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="block text-base font-semibold text-slate-900 mb-2">
                    Total Marks *
                  </label>
                  <input
                    type="number"
                    value={formData.total_marks}
                    onChange={(e) => setFormData({ ...formData, total_marks: e.target.value })}
                    placeholder="e.g., 20"
                    min="1"
                    step="0.5"
                    className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-base focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none transition-all"
                    required
                  />
                </div>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-purple-900">
                    <p className="font-semibold mb-1">Next Steps:</p>
                    <ul className="space-y-1">
                      <li>1. Upload or enter your answer key</li>
                      <li>2. Upload student test papers (up to 50)</li>
                      <li>3. AI will process and suggest marks</li>
                      <li>4. Review and approve all marks</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => navigate('/assessment')}
                  className="flex-1 bg-slate-100 text-slate-700 hover:bg-slate-200 px-6 py-3 rounded-xl font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className={`flex-1 px-6 py-3 rounded-xl font-semibold transition-all ${
                    creating
                      ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      : 'bg-purple-600 text-white hover:bg-purple-700 shadow-sm active:scale-95'
                  }`}
                >
                  {creating ? 'Creating...' : 'Create Project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default CreateMarkingProject;
